/**
 * MultiSelectDropdown — popover-style multi-select with search.
 *
 * Used for filters where the option list is too long for a chip
 * row (brand, model — fleets can carry 30+ models). The trigger
 * button shows the label + a count badge when anything's selected;
 * the popover holds a search box + a scrollable checkbox list +
 * "Clear / Done" actions.
 *
 * Behaviours:
 *   - Click outside the popover closes it (mouseDown listener on
 *     document, scoped to the wrapper ref).
 *   - Escape key closes it.
 *   - Empty options list still renders the trigger but the popover
 *     shows an inline empty-state.
 *
 * No external dropdown library — a single ref + a one-liner
 * useEffect keep the dependency footprint to zero.
 */
import { useEffect, useMemo, useRef, useState } from "react";

interface Props {
  readonly label: string;
  readonly options: readonly string[];
  readonly selected: readonly string[];
  readonly onChange: (next: readonly string[]) => void;
  /** Override for the placeholder shown when nothing is selected. */
  readonly placeholder?: string;
  /**
   * Hide the search box when the option list is small enough to scan.
   * Defaults to false; the parent toggles when the list is < 6 items.
   */
  readonly hideSearch?: boolean;
}

export function MultiSelectDropdown({
  label,
  options,
  selected,
  onChange,
  placeholder = "All",
  hideSearch = false,
}: Props): JSX.Element {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Close-on-outside-click. Tied to mouseDown so a click on the
  // trigger button doesn't get consumed by an outside-click handler
  // that toggled state mid-event.
  useEffect(() => {
    if (!open) return;
    function onDocMouseDown(e: MouseEvent): void {
      if (!wrapperRef.current) return;
      if (e.target instanceof Node && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent): void {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const filteredOptions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length === 0) return options;
    return options.filter((o) => o.toLowerCase().includes(q));
  }, [options, query]);

  function toggle(option: string): void {
    onChange(
      selected.includes(option) ? selected.filter((o) => o !== option) : [...selected, option],
    );
  }

  const triggerLabel =
    selected.length === 0
      ? placeholder
      : selected.length === 1
        ? selected[0]
        : `${selected.length} selected`;

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between rounded-lg border border-simuflex-line bg-white px-2.5 py-1.5 text-xs text-simuflex-navy hover:border-simuflex-navy/40"
      >
        <span className="flex items-center gap-2 truncate">
          {selected.length > 0 ? (
            <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-simuflex-navy text-white text-[10px] font-bold px-1">
              {selected.length}
            </span>
          ) : null}
          <span className="truncate">{triggerLabel}</span>
        </span>
        <svg
          className="w-3.5 h-3.5 text-simuflex-slate shrink-0"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d={open ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"}
          />
        </svg>
      </button>
      {open ? (
        <div
          role="dialog"
          aria-label={`${label} multi-select`}
          className="absolute z-30 mt-1 w-full min-w-[220px] rounded-xl border border-simuflex-line bg-white shadow-lg overflow-hidden"
        >
          {!hideSearch && options.length > 5 ? (
            <div className="border-b border-simuflex-line px-2 py-1.5">
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={`Search ${label.toLowerCase()}…`}
                className="w-full text-xs bg-transparent text-simuflex-navy placeholder:text-simuflex-slate/70 focus:outline-none"
                autoFocus
              />
            </div>
          ) : null}
          <div className="max-h-56 overflow-y-auto py-1">
            {filteredOptions.length === 0 ? (
              <p className="px-3 py-2 text-xs text-simuflex-slate italic">
                {options.length === 0 ? `No ${label.toLowerCase()} available.` : "No matches."}
              </p>
            ) : (
              filteredOptions.map((opt) => {
                const isSel = selected.includes(opt);
                return (
                  <label
                    key={opt}
                    className="flex items-center gap-2 px-2.5 py-1.5 text-xs hover:bg-simuflex-mist cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={isSel}
                      onChange={() => toggle(opt)}
                      className="h-3.5 w-3.5 rounded border-simuflex-line text-simuflex-navy"
                    />
                    <span className="text-simuflex-navy truncate">{opt}</span>
                  </label>
                );
              })
            )}
          </div>
          {selected.length > 0 ? (
            <div className="border-t border-simuflex-line flex items-center justify-between px-2.5 py-1.5">
              <button
                type="button"
                onClick={() => onChange([])}
                className="text-[10px] font-bold uppercase tracking-wider text-simuflex-slate hover:text-simuflex-navy"
              >
                Clear
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-[10px] font-bold uppercase tracking-wider text-simuflex-green hover:text-simuflex-forestGreen"
              >
                Done
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
